import { useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { 
  Search, 
  Loader2, 
  Package, 
  Clock, 
  CheckCircle, 
  XCircle, 
  CreditCard,
  Phone,
  Calendar,
  Hash,
  RefreshCw,
  Lock
} from "lucide-react";
import { Helmet } from "react-helmet";

interface OrderData {
  id: string;
  order_number: string;
  status: "pending" | "paid" | "delivered" | "cancelled" | "refunded";
  customer_whatsapp: string;
  total_amount: number;
  created_at: string;
  paid_at: string | null;
  delivered_at: string | null;
  product_name: string | null;
  product_duration: number | null;
  product_duration_unit: string | null;
}

const statusConfig = {
  pending: { 
    label: "Menunggu Pembayaran", 
    color: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
    icon: Clock
  },
  paid: { 
    label: "Sudah Dibayar", 
    color: "bg-blue-500/20 text-blue-500 border-blue-500/30",
    icon: CreditCard
  },
  delivered: { 
    label: "Terkirim", 
    color: "bg-green-500/20 text-green-500 border-green-500/30",
    icon: CheckCircle
  },
  cancelled: { 
    label: "Dibatalkan", 
    color: "bg-red-500/20 text-red-500 border-red-500/30",
    icon: XCircle
  },
  refunded: { 
    label: "Dikembalikan", 
    color: "bg-purple-500/20 text-purple-500 border-purple-500/30",
    icon: RefreshCw
  },
};

// Rate limiting constants
const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION_MS = 60 * 60 * 1000; // 1 hour
const RATE_LIMIT_STORAGE_KEY = 'order_lookup_rate_limit';

interface RateLimitState {
  attempts: number;
  blockedUntil: number | null;
}

const getRateLimitState = (): RateLimitState => {
  try {
    const stored = localStorage.getItem(RATE_LIMIT_STORAGE_KEY);
    if (stored) {
      const state = JSON.parse(stored) as RateLimitState;
      // Reset if lockout has expired
      if (state.blockedUntil && state.blockedUntil < Date.now()) {
        return { attempts: 0, blockedUntil: null };
      }
      return state;
    }
  } catch {
    // Ignore parsing errors
  }
  return { attempts: 0, blockedUntil: null };
};

const setRateLimitState = (state: RateLimitState) => {
  localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(state));
};

export default function TrackOrder() {
  const [searchType, setSearchType] = useState<"order" | "whatsapp">("order");
  const [searchValue, setSearchValue] = useState("");
  const [invoicePin, setInvoicePin] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState<OrderData[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [rateLimitState, setRateLimitStateLocal] = useState<RateLimitState>(getRateLimitState);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleSearch = async () => {
    // Check rate limiting
    const currentState = getRateLimitState();
    
    if (currentState.blockedUntil && currentState.blockedUntil > Date.now()) {
      const remainingMinutes = Math.ceil((currentState.blockedUntil - Date.now()) / 60000);
      toast.error(`Terlalu banyak percobaan. Coba lagi dalam ${remainingMinutes} menit.`);
      return;
    }
    
    if (!searchValue.trim()) {
      toast.error("Masukkan Order ID atau nomor WhatsApp");
      return;
    }

    if (!invoicePin.trim() || invoicePin.length !== 6) {
      toast.error("Masukkan PIN 6 digit yang valid");
      return;
    }

    setIsLoading(true);
    setHasSearched(true);
    
    try {
      let data: OrderData[] | null = null;

      if (searchType === "order") {
        // Use secure RPC function for order lookup
        const result = await supabase.rpc("get_order_by_number_and_pin", {
          _order_number: searchValue.trim().toUpperCase(),
          _invoice_pin: invoicePin.trim(),
        });
        
        if (result.error) throw result.error;
        data = result.data as OrderData[];
      } else {
        // Clean phone number for search
        const cleanedNumber = searchValue.trim().replace(/\D/g, "");
        
        // Use secure RPC function for WhatsApp lookup
        const result = await supabase.rpc("get_orders_by_whatsapp_and_pin", {
          _customer_whatsapp: cleanedNumber,
          _invoice_pin: invoicePin.trim(),
        });
        
        if (result.error) throw result.error;
        data = result.data as OrderData[];
      }

      setOrders(data || []);
      
      // Track failed attempts for rate limiting
      if (!data || data.length === 0) {
        const newAttempts = currentState.attempts + 1;
        const newState: RateLimitState = {
          attempts: newAttempts,
          blockedUntil: newAttempts >= MAX_ATTEMPTS ? Date.now() + LOCKOUT_DURATION_MS : null,
        };
        setRateLimitState(newState);
        setRateLimitStateLocal(newState);
        
        if (newAttempts >= MAX_ATTEMPTS) {
          toast.error("Terlalu banyak percobaan gagal. Akses diblokir selama 1 jam.");
        } else {
          toast.info(`Tidak ditemukan pesanan. Sisa percobaan: ${MAX_ATTEMPTS - newAttempts}`);
        }
      } else {
        // Reset attempts on successful search
        const newState: RateLimitState = { attempts: 0, blockedUntil: null };
        setRateLimitState(newState);
        setRateLimitStateLocal(newState);
      }
    } catch (err: any) {
      console.error("Error searching orders:", err);
      toast.error("Gagal mencari pesanan. Coba lagi.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <>
      <Helmet>
        <title>Cek Status Pesanan - Dagangan UMKM</title>
        <meta name="description" content="Lacak status pesanan Anda dengan memasukkan Order ID atau nomor WhatsApp dan PIN verifikasi." />
      </Helmet>

      <div className="min-h-screen flex flex-col bg-background">
        <Navbar />
        
        <main className="flex-1 container py-8">
          <div className="max-w-2xl mx-auto space-y-8">
            {/* Header */}
            <div className="text-center space-y-2">
              <h1 className="text-3xl font-bold text-foreground">Cek Status Pesanan</h1>
              <p className="text-muted-foreground">
                Lacak pesanan Anda dengan Order ID/WhatsApp dan PIN verifikasi
              </p>
            </div>

            {/* Search Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Search className="h-5 w-5 text-primary" />
                  Cari Pesanan
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Tabs value={searchType} onValueChange={(v) => setSearchType(v as "order" | "whatsapp")}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="order" className="gap-2">
                      <Hash className="h-4 w-4" />
                      Order ID
                    </TabsTrigger>
                    <TabsTrigger value="whatsapp" className="gap-2">
                      <Phone className="h-4 w-4" />
                      WhatsApp
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="order" className="mt-4 space-y-2">
                    <Label htmlFor="orderInput">Masukkan Order ID</Label>
                    <Input
                      id="orderInput"
                      placeholder="Contoh: DU250619-1234"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={handleKeyPress}
                      className="text-lg font-mono"
                    />
                  </TabsContent>

                  <TabsContent value="whatsapp" className="mt-4 space-y-2">
                    <Label htmlFor="whatsappInput">Masukkan Nomor WhatsApp</Label>
                    <Input
                      id="whatsappInput"
                      type="tel"
                      placeholder="Contoh: 08123456789"
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      onKeyDown={handleKeyPress}
                      className="text-lg"
                    />
                  </TabsContent>
                </Tabs>

                {/* PIN Input */}
                <div className="space-y-2 pt-2 border-t border-border">
                  <Label htmlFor="pinInput" className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-primary" />
                    PIN Verifikasi
                  </Label>
                  <Input
                    id="pinInput"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="Masukkan 6 digit PIN"
                    value={invoicePin}
                    onChange={(e) => setInvoicePin(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    onKeyDown={handleKeyPress}
                    className="text-lg font-mono tracking-widest text-center"
                  />
                  <p className="text-xs text-muted-foreground">
                    PIN diberikan saat Anda membuat pesanan
                  </p>
                </div>

                <Button 
                  className="w-full gradient-primary text-primary-foreground"
                  onClick={handleSearch}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Mencari...
                    </>
                  ) : (
                    <>
                      <Search className="h-4 w-4 mr-2" />
                      Cari Pesanan
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Results */}
            {hasSearched && (
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-foreground">
                  Hasil Pencarian ({orders.length})
                </h2>

                {orders.length === 0 ? (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Package className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground">
                        Tidak ditemukan pesanan dengan kriteria tersebut
                      </p>
                      <p className="text-sm text-muted-foreground/70 mt-1">
                        Pastikan Order ID/WhatsApp dan PIN yang Anda masukkan benar
                      </p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => {
                      const status = statusConfig[order.status];
                      const StatusIcon = status.icon;

                      return (
                        <Card key={order.id} className="overflow-hidden">
                          <div className={`h-1 ${order.status === 'delivered' ? 'bg-green-500' : order.status === 'paid' ? 'bg-blue-500' : order.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'}`} />
                          <CardContent className="pt-4 space-y-4">
                            {/* Order Header */}
                            <div className="flex items-start justify-between">
                              <div>
                                <p className="text-sm text-muted-foreground">Order ID</p>
                                <code className="text-lg font-mono font-bold text-foreground">
                                  {order.order_number}
                                </code>
                              </div>
                              <Badge className={`${status.color} border`}>
                                <StatusIcon className="h-3 w-3 mr-1" />
                                {status.label}
                              </Badge>
                            </div>

                            {/* Product Info */}
                            {order.product_name && (
                              <div className="p-3 bg-muted/50 rounded-lg">
                                <p className="font-medium text-foreground">{order.product_name}</p>
                                {order.product_duration && order.product_duration_unit && (
                                  <p className="text-sm text-muted-foreground">
                                    Durasi: {order.product_duration} {order.product_duration_unit}
                                  </p>
                                )}
                              </div>
                            )}

                            {/* Order Details */}
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Total</p>
                                <p className="font-bold text-primary">{formatPrice(order.total_amount)}</p>
                              </div>
                              <div>
                                <p className="text-muted-foreground">WhatsApp</p>
                                <p className="text-foreground">{order.customer_whatsapp}</p>
                              </div>
                            </div>

                            {/* Timeline */}
                            <div className="border-t border-border pt-4 space-y-2">
                              <div className="flex items-center gap-2 text-sm">
                                <Calendar className="h-4 w-4 text-muted-foreground" />
                                <span className="text-muted-foreground">Dibuat:</span>
                                <span className="text-foreground">{formatDate(order.created_at)}</span>
                              </div>
                              {order.paid_at && (
                                <div className="flex items-center gap-2 text-sm">
                                  <CreditCard className="h-4 w-4 text-blue-500" />
                                  <span className="text-muted-foreground">Dibayar:</span>
                                  <span className="text-foreground">{formatDate(order.paid_at)}</span>
                                </div>
                              )}
                              {order.delivered_at && (
                                <div className="flex items-center gap-2 text-sm">
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                  <span className="text-muted-foreground">Dikirim:</span>
                                  <span className="text-foreground">{formatDate(order.delivered_at)}</span>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}